<template>
  <v-card
    :class="[
      'd-flex flex-column mealCard',
      meal && meal.isAlt ? 'mt-2' : '',
      (!meal || !meal.id) ? 'emptyCard' : ''
    ]"
    :elevation="0"
    :loading="loading || saving"
  >
    <template #loader="{ isActive }">
      <div v-if="isActive || loading || saving" class="cardGlassLoader d-flex align-center justify-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
          width="3"
        />
      </div>
    </template>

    <v-dialog
      v-model="dialogRecipe"
      width="950"
      :fullscreen="$vuetify.display.smAndDown"
      scrollable
    >
      <recipe-details
        v-if="$vuetify.display.smAndUp"
        :key="recipe.id"
        :recipe="recipe"
        :title="recipe.name"
        :can-edit="false"
        :can-delete="false"
        @close="dialogRecipe = false"
        @update-score="(score)=>recipe.score = score"
      />
      <recipe-details-mobile
        v-if="$vuetify.display.xs"
        :key="recipe.id"
        :recipe="recipe"
        :title="recipe.name"
        :can-edit="false"
        :can-delete="false"
        @close="dialogRecipe = false"
        @update-score="(score)=>recipe.score = score"
      />
    </v-dialog>

    <v-dialog
      v-if="showScanner"
      v-model="showScanner"
      :fullscreen="$vuetify.display.smAndDown"
      width="500"
      scrollable
    >
      <v-card>
        <v-card-title class="text-h5 bg-primary text-white d-flex justify-space-between align-center">
          <span>Scannez une carte</span>
          <v-btn
            icon
            variant="text"
            size="small"
            title="Fermer"
            @click="showScanner = false"
          >
            <v-icon size="large">
              mdi-close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <live-scanner
            :active-scan="showScanner"
            @select="selectFromScan"
          >
            <template #append-card="scope">
              <div class="scanCardButtonContainer">
                <v-btn
                  v-if="scope.item.type !== 'custom'"
                  icon
                  size="x-small"
                  variant="outlined"
                  class="ml-2 scanCardButton"
                  @click="selectFromScan(scope.item, true)"
                >
                  <v-icon>
                    mdi-plus
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  size="x-small"
                  variant="outlined"
                  class="ml-2 mt-2 scanCardButton"
                  @click="selectFromScan(scope.item, false)"
                >
                  <v-icon>
                    mdi-sync
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </live-scanner>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="showScanner = false"
          >
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <app-modal
      v-model="showCommentDialog"
      title="Commentaire"
      width="500"
      :show-save="false"
      cancel-text="Fermer"
      body-class="pa-4 modalCommentText"
      @cancel="showCommentDialog = false"
    >
      {{ meal ? meal.comment : '' }}
    </app-modal>

    <app-modal
      v-model="showNameDialog"
      title="Repas"
      width="500"
      :show-save="false"
      cancel-text="Fermer"
      body-class="pa-4 modalCommentText"
      @cancel="showNameDialog = false"
    >
      {{ meal ? meal.name : '' }}
    </app-modal>

    <app-modal
      v-model="showLeftoverDialog"
      title="Repas source"
      width="500"
      :show-save="false"
      cancel-text="Fermer"
      body-class="pa-4 modalCommentText"
      @cancel="showLeftoverDialog = false"
    >
      {{ selectedLeftoverText }}
    </app-modal>

    <v-card-text v-if="meal && meal.id" :class="'mealSummary flex-grow-1 pa-0 ' + (meal && meal.isAlt ? 'alt' : '')">
      <div v-if="meal && meal.isAlt" class="altBadge">
        alt
      </div>

      <div v-if="meal.ignored" class="content pb-2">
        <div v-if="meal.ignored_type==='restaurant'" class="ignoredType">
          <v-img
            :src="restaurantImg"
            height="100px"
            cover
            class="ignoredImg"
          />
          <div class="nameWrapper pa-3">
            <div class="name">
              Restaurant
            </div>
          </div>
        </div>
        <div v-if="meal.ignored_type==='other'" class="ignoredType">
          <v-img
            :src="otherImg"
            height="100px"
            cover
            class="ignoredImg"
          />
          <div class="nameWrapper pa-3">
            <div class="name">
              Autre
            </div>
          </div>
        </div>
        <div v-if="meal.ignored_type==='leftover'" class="ignoredType">
          <v-img
            :src="leftoverImg"
            height="100px"
            cover
            class="ignoredImg"
          />
          <div class="nameWrapper pa-3 pb-1">
            <div class="name">
              Restes
            </div>
          </div>
          <ul v-if="meal.leftover_source_ids && meal.leftover_source_ids.length > 0" class="pl-7 pt-1 pr-3">
            <li v-for="lid in meal.leftover_source_ids" :key="lid">
              {{ displayedLeftoverMeal(lid) }}
              <a
                v-if="isLeftoverMealClamped(lid)"
                class="seeMoreLink text-primary text-caption ml-1"
                @click="openLeftoverDialog(lid)"
              >
                Voir tout
              </a>
            </li>
          </ul>
        </div>
        <div v-if="meal.comment" class="description upperFirstLetter px-3">
          <div class="commentText upperFirstLetter">
            {{ displayedComment }}
          </div>
          <div v-if="isCommentClamped" class="text-right">
            <a
              class="seeMoreLink text-primary text-caption"
              @click="showCommentDialog = true"
            >
              Voir tout
            </a>
          </div>
        </div>
      </div>

      <div v-if="!meal.ignored" class="content pb-2">
        <div class="nameWrapper pa-3">
          <div class="name upperFirstLetter">
            {{ displayedName }}
          </div>
          <div v-if="isNameClamped" class="text-right">
            <a
              class="seeMoreLink text-primary text-caption"
              @click="showNameDialog = true"
            >
              Voir tout
            </a>
          </div>
        </div>
        <div v-if="meal.comment" class="description upperFirstLetter px-3">
          <div class="commentText">
            {{ displayedComment }}
          </div>
          <div v-if="isCommentClamped" class="text-right">
            <a
              class="seeMoreLink text-primary text-caption"
              @click="showCommentDialog = true"
            >
              Voir tout
            </a>
          </div>
        </div>
        <v-row v-if="meal && meal.id" class="information flex-wrap px-3 my-0" justify="space-around">
          <v-col cols="auto" class="py-1 px-1">
            <VDropdown
              placement="top"
              :disabled="isOffline"
            >
              <v-chip
                link
                variant="tonal"
                color="secondary"
                size="small"
                title="Nombre de personnes"
              >
                <v-icon start size="small">
                  mdi-account-group
                </v-icon>
                {{ nb_people }}
              </v-chip>
              <template #popper>
                <v-container class="pa-3">
                  <int-stepper
                    v-model="nb_people"
                    :min="1"
                  />
                </v-container>
              </template>
            </VDropdown>
          </v-col>

          <v-col cols="auto" class="py-1 px-1">
            <VDropdown
              v-if="meal.composition.length > 0"
              placement="top"
            >
              <v-chip
                link
                variant="tonal"
                color="secondary"
                size="small"
                title="Ingrédients"
              >
                <v-icon start size="small">
                  mdi-food-apple
                </v-icon>
                {{ meal.composition.length }}
              </v-chip>
              <template #popper>
                <v-list v-if="meal.composition" class="ingList scrollbars">
                  <v-list-item
                    v-for="compo in meal.composition"
                    :key="compo.id"
                    class="ingredientContainer"
                  >
                    <template #prepend>
                      <v-img
                        v-if="compo.ingredient && compo.ingredient.image_url"
                        :src="compo.ingredient.image_url"
                        height="24"
                        width="24"
                        class="ma-0 mr-2"
                      />
                    </template>
                    <v-list-item-title :class="!compo.ingredient || !compo.ingredient.image_url ? 'ml-6 pl-2' : ''">
                      {{ compo.ingredient ? compo.ingredient.name : '' }}
                      {{ formatQuantity(compo.amount, compo.unit_shorthand) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </template>
            </VDropdown>
            <v-chip
              v-if="meal.composition.length === 0"
              variant="tonal"
              color="secondary"
              size="small"
            >
              <v-icon start size="small">
                mdi-food-apple
              </v-icon>
              {{ meal.composition.length }}
            </v-chip>
          </v-col>

          <v-col v-if="meal.recipes.length > 0" cols="auto" class="py-1 px-1">
            <VDropdown
              placement="top"
            >
              <v-chip
                variant="tonal"
                color="secondary"
                size="small"
                title="Recettes"
                :disabled="loadingRecipes"
                @click="toggleRecipes"
              >
                <v-icon start size="small">
                  mdi-pasta
                </v-icon>
                {{ meal.recipes.length }}
              </v-chip>
              <template #popper>
                <v-container v-if="meal.composition" class="pa-3 d-flex flex-column">
                  <v-btn
                    v-for="mealRecipe in mealRecipes"
                    :key="mealRecipe.id"
                    size="small"
                    variant="text"
                    :loading="loadingRecipe === mealRecipe.id"
                    @click="openRecipe(mealRecipe)"
                  >
                    {{ mealRecipe ? mealRecipe.name : '' }}
                    <v-icon size="small" class="ml-2">
                      mdi-eye
                    </v-icon>
                  </v-btn>
                </v-container>
              </template>
            </VDropdown>
          </v-col>
        </v-row>
      </div>

      <v-btn
        v-if="showAltBtn"
        color="primary"
        size="small"
        class="extraMealButton"
        title="Repas alternatif"
        rounded="xs"
        @click="create(true)"
      >
        <v-icon size="small" class="mr-2">
          mdi-plus-thick
        </v-icon>
        Alt
      </v-btn>
    </v-card-text>
    <v-card-text
      v-if="!meal || !meal.id"
      class="mealSummary empty flex-grow-1"
      @click="create(false)"
    >
      <div class="emptyPlaceholder">
        <v-icon size="48" class="emptyIcon mb-1">
          mdi-plus
        </v-icon>
        <span class="emptyLabel">
          Ajouter un plat
        </span>
      </div>

      <v-btn
        v-if="showAltBtn"
        color="primary"
        size="small"
        class="extraMealButton"
        title="Repas alternatif"
        rounded="xs"
        @click.stop="create(true)"
      >
        <v-icon size="small" class="mr-2">
          mdi-plus-thick
        </v-icon>
        Alt
      </v-btn>
    </v-card-text>
    <v-card-actions v-if="meal || canScanCode === true" class="actions pa-0">
      <v-btn
        v-if="meal && meal.id"
        icon
        color="primary"
        title="Modifier"
        :disabled="isOffline"
        size="small"
        variant="text"
        @click="edit"
      >
        <v-icon size="medium">
          mdi-pencil
        </v-icon>
      </v-btn>

      <v-spacer />

      <v-btn
        v-if="meal && meal.id"
        icon
        color="primary"
        title="Déplacer"
        :disabled="isOffline"
        size="small"
        class="mx-0"
        variant="text"
        @click="moveMeal(meal.id)"
      >
        <v-icon size="medium">
          mdi-swap-horizontal-bold
        </v-icon>
      </v-btn>
      <v-btn
        v-if="meal && meal.id"
        icon
        color="primary"
        title="Supprimer"
        :disabled="isOffline"
        size="small"
        class="mx-0"
        variant="text"
        @click="destroy(meal.id)"
      >
        <v-icon size="medium">
          mdi-delete
        </v-icon>
      </v-btn>
      <v-menu v-if="meal || canScanCode === true">
        <template #activator="{ props }">
          <v-btn
            icon
            color="primary"
            title="Plus"
            size="small"
            class="mx-0"
            variant="text"
            v-bind="props"
          >
            <v-icon size="medium">
              mdi-dots-horizontal
            </v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-if="canScanCode === true"
            :disabled="isOffline"
            @click="showScanner = true"
          >
            <template #prepend>
              <v-icon size="large" color="primary">
                mdi-qrcode-scan
              </v-icon>
            </template>
            Scanner
          </v-list-item>
          <reminder-button
            v-if="meal"
            :title="reminderInfo.title"
            :details="reminderInfo.details"
            :start="reminderInfo.start"
            :end="reminderInfo.end"
          />
        </v-list>
      </v-menu>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from "lodash"
import { useRepo } from 'pinia-orm'
import { Globalconfig } from "@/models/Globalconfig"
import { Meal, MealRepo } from "@/models/Meal"
import { Ingredient, IngredientRepo } from "@/models/Ingredient"
import { Recipe, RecipeRepo } from "@/models/Recipe"
import { RecipeIngredientRepo } from '@/models/RecipeIngredient'
import { RecipeMealRepo } from '@/models/RecipeMeal'
import PWA from "@/shared/pwa"
import ReminderButton from '@/components/ReminderButton'
import RecipeDetails from '@/components/RecipeDetails'
import RecipeDetailsMobile from '@/components/RecipeDetailsMobile'
import LiveScanner from '@/components/LiveScanner'
import IntStepper from '@/components/IntStepper'
import moment from 'moment'
import QrScanner from 'qr-scanner'
import { hideAllPoppers } from 'floating-vue'
import restaurantImg from '@/assets/new_restaurant_banner.jpg'
import otherImg from '@/assets/new_other_banner.jpg'
import leftoverImg from '@/assets/new_leftover_banner.jpg'

// Configuration de la limitation des caractères
const MAX_TITLE_CHARS = 100
const MAX_COMMENT_CHARS = 100
const MAX_LEFTOVER_CHARS = 50

export default {
  name: "MealSummaryCard",
  components: {
    ReminderButton,
    RecipeDetails,
    RecipeDetailsMobile,
    LiveScanner,
    IntStepper
  },
  props: {
    mealId: {
      type: Number,
      default: 0
    },
    type: {
      type: String,
      default: ""
    },
    date: {
      type: Date,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    showAltBtn: {
      type: Boolean,
      default: false
    },
  },
  emits: ['delete', 'moveMeal', 'createalt', 'create', 'edit'],
  data: () => ({
    saving: false,
    searchingIngredients: false,
    nbPeopleTimeout: null,
    mealRecipes: [],
    loadingRecipes: false,
    loadingRecipe: 0,

    dialogRecipe: false,
    recipe: {},

    canScanCode: false,
    showScanner: false,
    showCommentDialog: false,
    showNameDialog: false,
    showLeftoverDialog: false,
    selectedLeftoverText: '',

    MAX_TITLE_CHARS: MAX_TITLE_CHARS,
    MAX_COMMENT_CHARS: MAX_COMMENT_CHARS,
    MAX_LEFTOVER_CHARS: MAX_LEFTOVER_CHARS,

    restaurantImg,
    otherImg,
    leftoverImg
  }),
  computed: {
    isOffline() {
      return useRepo(Globalconfig).find('isOffline').value
    },
    meal() {
      return useRepo(MealRepo).withAllRecursive().find(this.mealId)
    },
    nb_people: {
      get() {
        const dnp = useRepo(Globalconfig).find("default_nb_people")
        const def = dnp ? dnp.value : 0
        return this.meal && this.meal.nb_people ? this.meal.nb_people : def
      },
      set(val) {
        this.updateNbPeople(val)
      }
    },
    isPwa() {
      return PWA.isPwa()
    },
    reminderInfo() {
      const strDate = moment(this.date).format('Y-MM-DD')
      const strName = this.meal && this.meal.name ? this.meal.name[0].toUpperCase() + this.meal.name.substring(1) : ''
      const start = moment(this.date).subtract(1, 'day').hours(19).minutes(0).seconds(0).toDate()
      return {
        title: 'Rappel pour '+strName,
        details: strName + "\n" + import.meta.env.VITE_SITE_URL+'/?date='+strDate,
        start: start,
        end: moment(start).add(1, 'hour').toDate()
      }
    },
    isCommentClamped() {
      return Boolean(this.meal && this.meal.comment && this.meal.comment.length > MAX_COMMENT_CHARS)
    },
    displayedComment() {
      if (!this.meal || !this.meal.comment) return ''
      if (this.isCommentClamped) {
        return this.meal.comment.slice(0, MAX_COMMENT_CHARS) + '...'
      }
      return this.meal.comment
    },
    isNameClamped() {
      return Boolean(this.meal && this.meal.name && this.meal.name.length > MAX_TITLE_CHARS)
    },
    displayedName() {
      if (!this.meal || !this.meal.name) return ''
      if (this.isNameClamped) {
        return this.meal.name.slice(0, MAX_TITLE_CHARS) + '...'
      }
      return this.meal.name
    }
  },
  mounted: async function () {
    this.checkCameraAvailability()
  },
  methods: {
    async checkCameraAvailability() {
      try {
        const hasCam = await QrScanner.hasCamera()
        this.canScanCode = hasCam
      }
      catch(e) {
        this.canScanCode = false
      }
    },
    async selectFromScan(item, addScan) {
      if(addScan) {
        if(item.type === 'ingredient') {
          if(!this.meal) {
            const meal = await useRepo(MealRepo).$create({
              name: useRepo(Ingredient).findByKey(item.id).name,
              date: this.date,
              type: this.type
            })
                        const newMealId = meal.id
            await useRepo(MealRepo).$updateIngredients(newMealId)
            this.$emit('edit')
          }
          else {
            await useRepo(MealRepo).$update(this.meal.id, {
              name: this.meal.name + ', ' + useRepo(Ingredient).findByKey(item.id).name
            })
            await useRepo(MealRepo).$updateIngredients(this.meal.id)
            this.$emit('edit')
          }
        }
        else if(item.type === 'recipe') {
          if(!this.meal) {
            const meal = await useRepo(MealRepo).$create({
              name: useRepo(Recipe).find(item.id).name,
              date: this.date,
              type: this.type,
              recipes: [
                {
                  recipe_id: item.id
                }
              ]
            })
            const newMealId = meal.id
            await useRepo(RecipeMealRepo).$getByMeal(newMealId)
            await useRepo(MealRepo).$updateIngredients(newMealId)
            this.$emit('edit')
          }
          else {
            await useRepo(MealRepo).$update(this.meal.id, {
              name: this.meal.name + ', ' + useRepo(Recipe).find(item.id).name,
              recipes: this.meal.recipes ? this.meal.recipes.concat([{recipe_id: item.id}]) : [{recipe_id: item.id}]
            })
            await useRepo(RecipeMealRepo).$getByMeal(this.meal.id)
            await useRepo(MealRepo).$updateIngredients(this.meal.id)
            this.$emit('edit')
          }
        }
      }
      else {
        // Replacing so this is once: close scanner
        this.showScanner = false

        const date = this.meal ? this.meal.date : this.date
        const type = this.meal ? this.meal.type : this.type
        const dnp = userRepo(Globalconfig).find("default_nb_people")
        const nb_people =dnp ? dnp.value : 1
        if(this.meal) {
          await useRepo(Meal).$delete(this.meal.id)
        }
        if(item.type === 'ingredient') {
          const meal = await useRepo(MealRepo).$create({
            name: useRepo(Ingredient).findByKey(item.id).name,
            date: this.date,
            type: this.type
          })
                    const newMealId = meal.id
          await useRepo(MealRepo).$updateIngredients(newMealId)
          this.$emit('edit')
        }
        else if(item.type === 'recipe') {
          const meal = await useRepo(MealRepo).$create({
            name: useRepo(Recipe).find(item.id).name,
            date: this.date,
            type: this.type,
            recipes: [
              {
                recipe_id: item.id
              }
            ]
          })
          const newMealId = meal.id
          await useRepo(RecipeMealRepo).$getByMeal(newMealId)
          await useRepo(MealRepo).$updateIngredients(newMealId)
          this.$emit('edit')
        }
        else if(item.type === 'custom') {
          await useRepo(MealRepo).$create({
            ignored: item.type === 'custom',
            comment: item.id === 'leftover' ? 'Restes' : (item.id === 'restaurant' ? 'Restaurant' : ''),
            date: this.date,
            type: this.type
          })
          this.$emit('edit')
        }
      }
    },
    formatQuantity(amount, unit_label) {
      const qtt = Ingredient.formatQuantity(amount, unit_label)
      if (qtt) {
        return "(" + qtt + ")"
      } else {
        return ""
      }
    },
    startSaving() {
      this.saving = true
    },
    endSaving() {
      this.saving = false
    },
    destroy(mealId) {
      this.$emit('delete', mealId)
    },
    moveMeal (mealId) {
      this.$emit('moveMeal', mealId)
    },
    updateNbPeople(val) {
      if (!this.meal || !this.meal.id || this.isOffline) return
      useRepo(MealRepo).save({
        id: this.meal.id,
        nb_people: val
      })
      if (this.nbPeopleTimeout) {
        clearTimeout(this.nbPeopleTimeout)
      }
      this.nbPeopleTimeout = setTimeout(async () => {
        this.saving = true
        try {
          await useRepo(MealRepo).$update(this.meal.id, {
            nb_people: val
          }, false)
          await useRepo(MealRepo).$updateIngredients(this.meal.id)
        } finally {
          this.saving = false
        }
      }, 600)
    },
    updateIngredients: async function() {
      if(this.meal && this.meal.id) {
        this.searchingIngredients = true
        await useRepo(MealRepo).$updateIngredients(this.meal.id)
        this.searchingIngredients = false
        this.$emit('edit')
      }
    },
    create: function(alt) {
      if(!this.isOffline) {
        if(alt) {
          this.$emit('createalt')
        }
        else {
          this.$emit('create')
        }
      }
    },
    edit: function() {
      this.$emit('edit', this.meal)
    },
    toggleRecipes: async function () {
      this.allowCloseRecipes = false
      if(this.showRecipes) {
        this.showRecipes = false
      }
      else {
        this.loadingRecipes = true
        const ids = this.meal.recipes.reduce((prev, curr) => {
          // Try to find it in store before querying
          const inStore = useRepo(Recipe).find(curr.recipe_id)
          if(!inStore) {
            prev.push(curr.recipe_id)
          }
          return prev
        }, [])
        if(ids.length > 0) {
          await useRepo(RecipeRepo).$fetch({
            ids: ids
          })
        }
        const recipes = useRepo(Recipe).find(this.meal.recipes.map(r => r.recipe_id))
        this.mealRecipes = recipes || []
        this.loadingRecipes = false
        this.showRecipes = true
      }
    },
    openRecipe: async function(recipe) {
      this.loadingRecipe = recipe.id
      await Promise.all([
        useRepo(IngredientRepo).$getByRecipe(recipe.id),
        useRepo(RecipeIngredientRepo).$getByRecipe(recipe.id)
      ])
      this.loadingRecipe = 0
      hideAllPoppers()
      this.recipe = useRepo(Recipe).withAllRecursive().find(recipe.id)
      this.dialogRecipe = true
    },
    formatDate(date) {
      return moment.utc(date).format('DD/MM/YY')
    },
    formatMeal(id, defaultValue) {
      const meal = useRepo(Meal).find(id)
      return meal ? meal.summary : (defaultValue ? defaultValue : '')
    },
    isLeftoverMealClamped(id) {
      const text = this.formatMeal(id, 'Repas supprimé')
      return Boolean(text && text.length > MAX_LEFTOVER_CHARS)
    },
    displayedLeftoverMeal(id) {
      const text = this.formatMeal(id, 'Repas supprimé')
      if (this.isLeftoverMealClamped(id)) {
        return text.slice(0, MAX_LEFTOVER_CHARS) + '...'
      }
      return text
    },
    openLeftoverDialog(id) {
      this.selectedLeftoverText = this.formatMeal(id, 'Repas supprimé')
      this.showLeftoverDialog = true
    },
    formatTitle(title) {
      return _.upperFirst(title)
    }
  }
}
</script>

<style lang="scss" scoped>
.mealCard {
  overflow: visible;
  max-width: 100%;
  position: relative;
  z-index: 1;
  border-radius: 16px !important;
  transition: background-color 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  :deep(.v-card__loader) {
    inset: 0;
    height: 100%;
    z-index: 10;
    pointer-events: none;
  }

  .cardGlassLoader {
    width: 100%;
    height: 100%;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    pointer-events: auto;
  }

  &:not(.emptyCard) {
    background: rgba(255, 255, 255, 0.75) !important;
    backdrop-filter: blur(12px) saturate(160%);
    -webkit-backdrop-filter: blur(12px) saturate(160%);
    border: 1px solid rgba(var(--v-theme-primary), 0.18) !important;
    box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.05), 0 2px 6px -2px rgba(0, 0, 0, 0.02) !important;

    &:hover {
      z-index: 5;
      background: rgba(255, 255, 255, 0.92) !important;
      box-shadow: 0 12px 26px -4px rgba(var(--v-theme-primary), 0.14), 0 4px 10px -2px rgba(0, 0, 0, 0.05) !important;
      border-color: rgba(var(--v-theme-primary), 0.55) !important;
    }
  }

  &.emptyCard {
    background-color: transparent !important;
    border: 2px dotted rgba(var(--v-theme-secondary), 0.5);
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      border-color: rgb(var(--v-theme-primary));
      background-color: rgba(var(--v-theme-primary), 0.04) !important;

      .emptyIcon,
      .emptyLabel {
        color: rgb(var(--v-theme-primary));
      }
    }

    .actions {
      position: absolute;
      bottom: 2px;
      right: 2px;
      border-top: none;
      z-index: 1;
    }
  }
}
.mealSummary {
  min-height: 230px;
  height: 100%;
  display: flex;
  align-items: stretch;

  &.alt {
    min-height: 100px;
    margin-bottom: 30px;

    .nameWrapper,
    .ignored {
      padding-right: 52px;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    width: 100%;

    .name {
      font-size: 1.5em;
      font-weight: 600;
      line-height: 1.25em;
      letter-spacing: -0.01em;
      color: rgb(var(--v-theme-primary));
      word-break: break-word;
    }

    .nameWrapper {
      flex-grow: 1;
      padding-bottom: 10px;

      .seeMoreLink {
        display: inline-block;
        margin-top: 4px;
        cursor: pointer;
        text-decoration: underline;
      }
    }

    .description {
      flex-grow: 0;
      flex-shrink: 0;
      margin-top: 12px;
      white-space: pre-line;

      .commentText {
        word-break: break-word;
      }

      .seeMoreLink {
        display: inline-block;
        margin-top: 4px;
        cursor: pointer;
        text-decoration: underline;
      }
    }

    .information {
      flex-grow: 0;

      display: flex;
      justify-content: space-around;
    }
  }

  &.empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;

    .emptyPlaceholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease-in-out;
    }

    .emptyIcon {
      color: rgb(var(--v-theme-secondary));
      transition: color 0.2s ease-in-out;
    }

    .emptyLabel {
      font-size: 0.9rem;
      font-weight: 500;
      color: rgb(var(--v-theme-secondary));
      transition: color 0.2s ease-in-out;
      text-align: center;
    }

    &:hover .emptyPlaceholder {
      transform: scale(1.05);
    }
  }
}

.actions {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  gap: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  .v-btn {
    opacity: 0.8;
    transition: opacity 0.2s ease, transform 0.2s ease;

    &:hover {
      opacity: 1;
      transform: scale(1.08);
    }
  }
}

.ingredient {
  padding: 2px 2px 2px 6px;
  margin-right: 3px;
  display: inline-block;
}

.ingList {
  max-height: 250px;
  overflow: auto;
}

.extraMealButton {
  position: absolute;
  left: 50%;
  width: 80px;
  margin-left: -40px;
  bottom: -28px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.altBadge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgb(var(--v-theme-primary));
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-top-right-radius: 15px;
  border-bottom-left-radius: 10px;
  z-index: 2;
  line-height: 1.2;
}

.ingredientContainer {
  min-height: 28px;
}

.ignoredType {
  .ignoredImg {
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    overflow: hidden;
  }

  .seeMoreLink {
    cursor: pointer;
    text-decoration: underline;
  }
}

.scanCardButtonContainer {
  display: inline-block;
}
.scanCardButton {
  display: block;
}

.lastMeals {
  max-height: 200px;
  overflow: auto;
}

.modalCommentText {
  white-space: pre-line;
  word-break: break-word;
}

.ellipsedTitle {
  display: inline-block;
  vertical-align: middle;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>